import React, { useState, useEffect, lazy, Suspense } from 'react';
import Wordmark from '../common/Wordmark';
import { useAuth } from '../../context/AuthContext';
import { PLANS, DEMOS, planSlug } from '../common/plans';

// Below-the-fold / on-interaction widgets: keep them out of the landing chunk so
// the hero paints sooner (PageSpeed listed AskSangs + the voice agent among the
// biggest unused-JS items on first load).
const AskSangs = lazy(() => import('../AskSangs/AskSangs'));
const BookCallVoiceAgent = lazy(() => import('../common/BookCallVoiceAgent'));
const DemoVideoModal = lazy(() => import('../common/DemoVideoModal'));

// Hero headline rotator — genre, school-type suffix, and badge cycle together
// so "Karate" never renders over the South Asian dance badge.
const SA_BADGE = 'Built for the South Asian dance community';
const OWNER_BADGE = 'Built for independent studio owners';
const HERO_ROTATION = [
  { word: 'Bharatanatyam', suffix: 'dance school.', badge: SA_BADGE },
  { word: 'Karate', suffix: 'dojo.', badge: OWNER_BADGE },
  { word: 'Kathak', suffix: 'dance school.', badge: SA_BADGE },
  { word: 'Piano', suffix: 'music studio.', badge: OWNER_BADGE },
  { word: 'Kuchipudi', suffix: 'dance school.', badge: SA_BADGE },
  { word: 'Pilates', suffix: 'studio.', badge: OWNER_BADGE },
  { word: 'Odissi', suffix: 'dance school.', badge: SA_BADGE },
  { word: 'Yoga', suffix: 'studio.', badge: OWNER_BADGE },
];

const useHeroRotation = (items, interval = 2200) => {
  const [i, setI] = useState(0);
  const [show, setShow] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setShow(false);
      setTimeout(() => { setI((p) => (p + 1) % items.length); setShow(true); }, 250);
    }, interval);
    return () => clearInterval(t);
  }, [items.length, interval]);
  return { item: items[i], show };
};

const fadeStyle = (show) => ({
  transition: 'opacity .25s ease, transform .25s ease',
  opacity: show ? 1 : 0,
  transform: show ? 'translateY(0)' : 'translateY(0.3em)'
});

// Reserve the width of the LONGEST word with a hidden sizer and overlay the
// animated word on top — so the line width never changes and "Manage your"
// stays put as the genre cycles.
const RollingWord = ({ word, show, className = '' }) => {
  const longest = HERO_ROTATION.reduce((a, b) => (b.word.length > a.word.length ? b : a)).word;
  return (
    <span style={{ position: 'relative', display: 'inline-block', textAlign: 'left' }}>
      <span aria-hidden="true" style={{ visibility: 'hidden' }}>{longest}</span>
      <span
        className={className}
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 0,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          ...fadeStyle(show)
        }}
      >
        {word}
      </span>
    </span>
  );
};

// "Sound familiar?" — real owner pains mapped to the feature that solves each.
const PROBLEMS = [
  { title: 'Class Reminders', problem: 'Making sure everyone knows there’s a class tomorrow.', solution: 'DanceHall Studios automatically sends class reminders to students the evening before class.' },
  { title: 'Attendance Tracking & Reminders', problem: 'Knowing how many classes a student has attended before qualifying them for an assessment, exam, or recital.', solution: 'Students scan a unique QR code for their class and attendance is tracked automatically. Students falling behind get email reminders nudging them to attend the next class.' },
  { title: 'Payment Collection & Tracking', problem: 'Depending on parents or family members to collect monthly payments — and accidentally missing some.', solution: 'Automatic monthly deductions with the ability to pause when needed. Students can scan a QR code and the payment method on file is charged automatically.' },
  { title: 'Calendar Management', problem: 'Juggling studio calendars, work calendars, personal calendars, and notifications for yourself and your students.', solution: 'Integrate your Gmail or Outlook calendar. Sangs, our AI assistant, learns your schedule and acts like a chief of staff — helping suggest, manage, and control your workload.' },
  { title: 'Group Messaging', problem: 'Not wanting to put everyone in a WhatsApp group where students and parents can see each other’s phone numbers.', solution: 'Create moderated chat groups inside DanceHall Studios so students and parents can communicate while their phone numbers stay private.' },
  { title: 'Online Payments & Follow-Up', problem: 'Running online classes globally and needing to know who dropped off, who paid, and who needs follow-up.', solution: 'Collect payments from students in 98 countries. DanceHall sends email follow-ups to students who drop off mid-enrollment — recovering lost signups and revenue.' },
  { title: 'Lead Collection & Follow-Up', problem: 'Launching new classes but struggling to track Instagram DMs and follow up with interested students.', solution: 'DanceHall collects emails as waitlists, sends messages and emails, makes follow-up calls when new classes open, and helps sign people up on your behalf.' },
  { title: 'Lead Collection & Follow-Up', problem: 'Going back and forth with prospective students and parents through Instagram DMs.', solution: 'DanceHall captures every inquiry, nurtures them with timely follow-ups, and helps convert them into enrollments.' },
  { title: 'Budgeting Tool', problem: 'Keeping multiple spreadsheets just to calculate costs and budgets.', solution: 'Upload any receipt, tell Sangs the expense, or even email it to Sangs — and it builds a unified spending sheet for your school’s budgeting.' },
];

// Real customer quotes (Temple of Fine Arts, HipHop Natyam).
const TESTIMONIALS = [
  { quote: 'We launched a whole new online program — and with DanceHall we could communicate through in-house emails, set reminders, and take payments. Now we’re even using it to set assignments. All in one place.', name: 'Director, Temple of Fine Arts', location: 'Texas' },
  { quote: 'DanceHall rocks! We ask for changes and they’re done the same day — no ticket queue.', name: 'HipHop Natyam Studios', location: '' },
];

const faqs = [
  {
    q: 'How much does DanceHall Studios cost?',
    a: 'Two simple monthly plans, plus a custom option: Studio ($49) — a booking page + calendar that collects fees for your classes or packages; Studio Pro ($149) to run your whole studio; and Custom, for organizations that need newsletters, donor management, ticketing, or a build specific to them — we price that on a call. Your families pay tuition only — you keep your own Stripe account, your brand, and your bank. Every plan starts with a quick call so we set you up on the right one.',
  },
  {
    q: 'How do payments work?',
    a: 'Powered by Stripe. Students pay online with credit card or ACH bank transfer. Monthly recurring, quarterly, or one-time — money goes directly to your bank account.',
  },
  {
    q: 'Can I pause students who travel for the summer?',
    a: 'Yes. Pause any student without cancelling the relationship — their billing stops, their access holds, and you can unpause them the moment they return. Common for long-term students who spend summers in India.',
  },
  {
    q: 'Can I organize courses by level and promote students between levels?',
    a: 'Yes. Structure your curriculum by level, track each student\u2019s progress, and promote them at the next billing cycle with a single click. Promotion emails go out automatically.',
  },
  {
    q: 'How do announcements and newsletters work?',
    a: 'Send class-wide or school-wide emails from your own sender address with rich text, images, and pre-built templates. Everything goes out through a proper inbox trail — no WhatsApp group scroll required.',
  },
  {
    q: 'Is there a contract or lock-in?',
    a: "No contracts, no lock-in, no hidden fees. You can stop using DanceHall Studios at any time. Your students' payments are processed directly through your Stripe account.",
  },
  {
    q: 'Will my students remember their classes?',
    a: 'Yes. DanceHall automatically sends a class reminder the evening before each class, so families always know there’s a class tomorrow — no manual texts or WhatsApp pings.',
  },
  {
    q: 'Can I track attendance toward exams, assessments, and arangetrams?',
    a: 'Yes. Students scan a unique QR code at each class and attendance is logged automatically. You can see exactly how many classes a student has attended before qualifying them for an assessment, exam, or recital — and students who fall behind get email reminders nudging them to attend the next class.',
  },
  {
    q: 'Can parents and students message without sharing phone numbers?',
    a: 'Yes. Create moderated chat groups inside DanceHall so students and parents can communicate while their phone numbers stay private — no WhatsApp group where everyone sees each other’s number.',
  },
  {
    q: 'Can I collect tuition from students in other countries?',
    a: 'Yes. DanceHall collects payments from students in 98 countries. For online and global classes, it can also automatically follow up with anyone who drops off mid-enrollment to recover lost signups.',
  },
  {
    q: 'I get a lot of inquiries through Instagram DMs — can DanceHall help?',
    a: 'Yes. DanceHall captures every inquiry, collects emails as waitlists, and nurtures interested students with timely follow-ups — even helping sign them up on your behalf when new classes open, so you stop going back and forth in DMs.',
  },
  {
    q: 'Can it help with budgeting and tracking expenses?',
    a: 'Yes. Upload a receipt, tell Sangs an expense, or even email it in — Sangs builds a unified spending sheet for your school’s budgeting, so you’re not juggling multiple spreadsheets.',
  },
  {
    q: 'I’m switching from Jackrabbit or DanceStudio-Pro — can you help me move?',
    a: 'Yes. We help you bring over your roster and set up payments. Depending on your current processor, saved payment methods can often be migrated securely so families don’t all have to re-enter their cards. Book a demo and we’ll map out your move.',
  },
];

// "Book a call" — the meet-before-booking flow. A thin modal shell around the
// reusable BookCallVoiceAgent: a real-time voice conversation with Sangs that
// ends in a booked call (Calendly). Layered fallback lives inside the agent —
// voice → text chat → static form — so the flow always completes.
const BookCallModal = ({ open, initialPlan = 'unsure', onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg my-8 p-6 sm:p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-2xl leading-none z-10">&times;</button>
        <Suspense fallback={<div className="p-8 text-sm text-gray-400">Loading…</div>}>
          <BookCallVoiceAgent initialPlan={initialPlan} source="landing-book-call" />
        </Suspense>
      </div>
    </div>
  );
};

// "Sound familiar?" carousel — auto-scrolls slowly AND is hand-movable: drag
// with the mouse on desktop, native swipe on mobile. Cards are duplicated so
// the loop is seamless; auto-scroll pauses while hovered / dragged / touched.
// Slow right-to-left drift for a duplicated list, so the loop is seamless. Shared
// by the problems carousel and the demo strip — one implementation, one behaviour:
// pauses on hover/touch/drag and does nothing at all under prefers-reduced-motion.
const useDriftScroll = (ref, paused, pxPerSecond = 38) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf; let last = performance.now();
    const tick = (now) => {
      const dt = now - last; last = now;
      if (!paused.current && el.scrollWidth > el.clientWidth) {
        el.scrollLeft += (pxPerSecond * dt) / 1000;
        const half = el.scrollWidth / 2;   // list is rendered twice
        if (el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ref, paused, pxPerSecond]);
};

const ProblemsCarousel = () => {
  const ref = React.useRef(null);
  const paused = React.useRef(false);
  const drag = React.useRef({ active: false, startX: 0, startLeft: 0 });

  useDriftScroll(ref, paused);

  const wrap = () => {
    const el = ref.current; if (!el) return;
    const half = el.scrollWidth / 2;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft < 0) el.scrollLeft += half;
  };
  const down = (e) => {
    if (e.pointerType !== 'mouse') return; // touch uses native scroll
    const el = ref.current;
    drag.current = { active: true, startX: e.clientX, startLeft: el.scrollLeft };
    paused.current = true;
    el.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => {
    if (!drag.current.active) return;
    ref.current.scrollLeft = drag.current.startLeft - (e.clientX - drag.current.startX);
    wrap();
  };
  const up = () => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setTimeout(() => { paused.current = false; }, 700);
  };

  return (
    <div className="relative overflow-hidden -mx-6">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-20 z-10 bg-gradient-to-r from-[#faf0f0] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-20 z-10 bg-gradient-to-l from-[#faf0f0] to-transparent" />
      <div
        ref={ref}
        onMouseEnter={() => { paused.current = true; }}
        onMouseLeave={() => { if (!drag.current.active) paused.current = false; }}
        onTouchStart={() => { paused.current = true; }}
        onTouchEnd={() => { setTimeout(() => { paused.current = false; }, 1000); }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        className="flex gap-5 px-6 py-2 overflow-x-auto select-none cursor-grab active:cursor-grabbing [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {[...PROBLEMS, ...PROBLEMS].map((p, i) => (
          <div key={i} aria-hidden={i >= PROBLEMS.length} className="shrink-0 w-[300px] sm:w-[380px] flex flex-col bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
            <p className="text-gray-800 font-medium leading-relaxed mb-4">
              <span className="text-brand-300 font-heading text-xl mr-1 align-[-0.15em]">&ldquo;</span>
              {p.problem}
            </p>
            <span className="inline-block self-start text-[11px] font-semibold uppercase tracking-wide text-brand-700 bg-brand-50 px-3 py-1 rounded-full mb-3">
              {p.title}
            </span>
            <p className="text-gray-500 leading-relaxed">{p.solution}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// The two onboarding tracks, tab-switched: pick a plan, read its steps, and watch
// that plan's demo right there. Replaces a drifting thumbnail strip — with only two
// videos the motion was decoration, and it moved the click target.
//
// The video plays INLINE and large (roughly half the screen on a phone, ~16:9 and
// wide on desktop) rather than in a modal, because it's the proof for the steps
// directly above it; sending it to a lightbox separated the claim from the evidence.
const TRACKS = [
  {
    key: 'invoicing', plan: 'Studio', price: '$49', tone: 'brand',
    lede: 'Self-serve. No call needed.',
    demo: DEMOS.bookings,
    // Payments are Stripe only. Venmo/Zelle were removed 2026-08-13: neither has a
    // usable API (Venmo's developer + payouts APIs are retired to new businesses;
    // Zelle is bank-to-bank with no third-party integration), so there is no way to
    // confirm a payment landed. Do NOT put them back in this copy without a rail.
    caption: 'Build the page, connect Stripe, share the link.',
    steps: [
      ['Build your booking page', 'Classes, packages, your prices.'],
      ['Connect payments', 'Stripe — cards and bank transfer, into your own account.'],
      ['Share your link', 'One page; students pick a class and pay.'],
      ['Get paid', 'Sangs chases anything unpaid for you.'],
    ],
  },
  {
    key: 'studio', plan: 'Studio Pro', price: '$149', tone: 'saffron',
    lede: 'We set it up for you, in under 24 hours.',
    demo: DEMOS.studio,
    caption: 'Send your class list; your whole studio comes up running.',
    steps: [
      ['Send your class list', 'Roster, levels, prices — however you keep them.'],
      ['We migrate everyone', 'Students land in the right class, already enrolled.'],
      ['Your studio goes live', 'Portal, attendance, materials, reminders.'],
      ['It runs itself', 'Sangs handles reminders, follow-ups and pauses.'],
    ],
  },
];

const HowItWorks = () => {
  const [active, setActive] = useState('invoicing');
  const track = TRACKS.find((t) => t.key === active) || TRACKS[0];
  const isBrand = track.tone === 'brand';

  // Colour coding carried by BACKGROUND + BORDER, never coloured text: measured,
  // every saffron-on-saffron and white-on-saffron pair lands between 2.1:1 and
  // 4.2:1 — under the 4.5:1 AA floor at this size. Dark ink on saffron-500 is 8.3:1.
  const dot = isBrand
    ? 'bg-brand-50 border-brand-200 text-brand-800'
    : 'bg-saffron-100 border-saffron-500/60 text-gray-900';

  return (
    <>
      {/* Tabs */}
      <div className="flex justify-center gap-2 mb-8" role="tablist" aria-label="Choose a plan">
        {TRACKS.map((t) => {
          const on = t.key === active;
          const onCls = t.tone === 'brand' ? 'bg-brand-900 text-white' : 'bg-saffron-500 text-gray-900';
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={on}
              onClick={() => setActive(t.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                on ? onCls : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
              }`}
            >
              {t.plan} &middot; {t.price}/mo
            </button>
          );
        })}
      </div>

      <p className="text-center text-sm text-gray-500 mb-6">{track.lede}</p>

      {/* Steps — compact and horizontal; side-scrolls below sm rather than becoming
          a full-width wall of text on a phone. */}
      <ol className="flex gap-3 overflow-x-auto pb-2 mb-8 sm:grid sm:grid-cols-4 sm:gap-4 sm:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {track.steps.map(([title, desc], i) => (
          <li key={title} className="shrink-0 w-[190px] sm:w-auto">
            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center mb-2 ${dot}`}>
              <span className="font-heading text-sm">{i + 1}</span>
            </div>
            <h3 className="font-semibold text-sm text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-500 leading-snug">{desc}</p>
          </li>
        ))}
      </ol>

      {/* The demo for the selected plan. `key` forces a fresh <video> per tab so
          switching tabs never leaves the previous plan's audio playing. */}
      <div className="max-w-3xl mx-auto">
        <video
          key={track.key}
          src={track.demo.src}
          poster={track.demo.poster}
          controls
          playsInline
          preload="metadata"
          className={`w-full rounded-2xl border border-gray-200 bg-black ${
            // Portrait demo: cap the height so a 9:16 video doesn't run off the page.
            // Landscape: let it sit at its natural 16:9.
            track.demo.orientation === 'portrait'
              ? 'h-[50vh] sm:h-[560px] object-contain'
              : 'aspect-video'
          }`}
        />
        <p className="text-sm text-gray-500 text-center mt-3">{track.caption}</p>
      </div>
    </>
  );
};

const LandingPage = () => {
  const { item: hero, show: heroShow } = useHeroRotation(HERO_ROTATION);
  // Links from other pages (blog, legal, grants) arrive as /#pricing, /#faq,
  // /#features. The browser tries to jump to the hash on load, but at that
  // moment nothing is rendered yet (React mounts after, and RootRedirect waits
  // on auth), so the jump silently no-ops. Retry until the target exists.
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace('#', '');
      if (!id) return;
      let tries = 0;
      const tick = () => {
        const el = document.getElementById(id);
        if (el) {
          // Instant, like a native hash jump — and explicit maths for the fixed
          // 64px nav because scroll-margin only applies to scrollIntoView, and
          // smooth scrolling is skipped by Chrome in a background tab.
          const top = el.getBoundingClientRect().top + window.scrollY - 64;
          window.scrollTo({ top, behavior: 'instant' });
          return;
        }
        if (tries++ < 40) setTimeout(tick, 100);   // up to ~4s for auth + render
      };
      tick();
    };
    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [demo, setDemo] = useState(null); // which plan's demo video is open
  const [bookPlan, setBookPlan] = useState('unsure');
  const { login } = useAuth();

  // Meet-before-booking: open the Book-a-call modal, optionally pre-selecting a tier.
  const openBook = (plan = 'unsure') => { setBookPlan(plan); setBookOpen(true); };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="min-h-screen bg-[#faf0f0] font-body">

      {/* ─── Nav ─── */}
      <nav className="fixed top-0 w-full bg-[#faf0f0]/80 backdrop-blur-md z-50 border-b border-gray-200/60">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Wordmark />

          <div className="hidden md:flex items-center gap-8 text-[13px] text-gray-500">
            <a href="#pricing" className="hover:text-gray-900 transition-colors">Plans</a>
            <a href="#faq" className="hover:text-gray-900 transition-colors">FAQ</a>
            <a href="/blog" className="hover:text-gray-900 transition-colors">Blog</a>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => login()}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Log in
            </button>
            {/* Get Started goes to the plans, not straight to signup — we want to
                know which tier they're choosing before they talk to Sangs. */}
            <a
              href="#pricing"
              className="text-sm font-medium bg-brand-900 text-white px-5 py-2 rounded-full hover:bg-brand-800 transition-colors"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="pt-32 pb-20 md:pt-44 md:pb-32 px-6 relative overflow-hidden">
        {/* Subtle gradient wash */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-gradient-to-br from-brand-100/40 via-transparent to-saffron-100/30 rounded-full blur-3xl pointer-events-none" />

        {/* TODO: pencil-drawn 2D hero illustration goes here (absolute, behind content) */}

        <div className="max-w-5xl mx-auto text-center relative">
          {/* Badge — text rotates in sync with the headline. A hidden sizer
              (the longest badge) keeps the pill from resizing on each swap. */}
          <div className="relative inline-flex items-center bg-brand-50 border border-brand-200/80 text-brand-800 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <span aria-hidden="true" className="invisible whitespace-nowrap">{SA_BADGE}</span>
            <span
              className="absolute inset-0 flex items-center justify-center whitespace-nowrap"
              style={fadeStyle(heroShow)}
            >
              {hero.badge}
            </span>
          </div>

          <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mb-8 leading-[1.12]">
            Manage your
            <br />
            <RollingWord word={hero.word} show={heroShow} className="text-brand-900" />
            <br />
            <span style={fadeStyle(heroShow)} className="inline-block">{hero.suffix}</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
            Payments, newsletters, class reminders, level promotions, and student
            pauses &mdash; handled. Your students &mdash; and their parents &mdash;
            log in directly from your website. So you can focus on teaching, not
            chasing spreadsheets and WhatsApp threads.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {/* One action, not two. Both buttons now lead to the same place — the
                Get Started popup — because every plan starts with a call, so a
                self-serve "Create Your Studio" would skip the lead capture the
                whole funnel is built on. Log in still lives in the header. */}
            <button
              type="button"
              onClick={() => openBook()}
              className="w-full sm:w-auto bg-brand-900 text-white border border-transparent px-8 py-4 rounded-full text-lg font-semibold hover:bg-brand-800 transition-all hover:shadow-lg hover:shadow-brand-900/25"
            >
              Get Started
            </button>
          </div>

        </div>
      </section>

      {/* ─── Social Proof ─── */}
      <section className="pt-2 pb-16 md:pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-6">
            Studios running on DanceHall
          </p>
          {/* Real logos, pulled from each studio's uploaded branding.
              mix-blend-multiply lets the white-background JPG (TFATX) sit on
              the pink page without a visible box. Wraps to a column at 375px. */}
          <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-8 sm:gap-12 md:gap-16">
            <a
              href="https://www.templeoffineartstexas.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              {/* No opacity on this link: opacity < 1 isolates the blend and the
                  JPG's white background would show as a box. */}
              <img
                src="/brand/tfatx/logo.jpg"
                width={421}
                height={376}
                alt="Temple of Fine Arts Texas"
                className="h-24 md:h-28 w-auto object-contain mix-blend-multiply"
                loading="lazy"
              />
            </a>
            <a
              href="https://hiphopnatyam.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src="/brand/hiphop-natyam/logo-full.png"
                width={384}
                height={546}
                alt="HipHopNatyam"
                className="h-28 md:h-32 w-auto object-contain mix-blend-multiply"
                loading="lazy"
              />
            </a>
            <a
              href="https://www.navatman.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img
                src="/brand/navatman/logo.png"
                width={799}
                height={209}
                alt="Navatman"
                className="h-10 md:h-12 w-auto object-contain"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </section>

      {/* ─── Problems We Solve ─── */}
      <section id="features" className="py-20 md:py-28 px-6 bg-[#faf0f0] scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">Sound familiar?</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Every headache a studio owner runs into &mdash; and exactly how DanceHall handles it.
            </p>
          </div>
          <ProblemsCarousel />
        </div>
      </section>

      {/* ─── Features (bento grid) ─── */}
      <section id="pricing" className="py-20 md:py-28 px-6 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <h2 className="font-heading text-3xl md:text-5xl text-gray-900 mb-4">Simple plans. Start with a call.</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Every plan starts with a quick call &mdash; we learn how you run things today and set you up on the right one.
              Your families pay tuition only; you keep your own brand and bank. We just charge a flat monthly fee.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12 items-stretch">
            {PLANS.map((plan) => (
              <div key={plan.name} className={`relative rounded-2xl p-7 md:p-8 flex flex-col ${plan.highlight ? 'bg-white border-2 border-brand-500 shadow-lg shadow-brand-900/10' : 'bg-[#faf0f0] border border-gray-100'}`}>
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-900 text-white text-xs font-semibold px-3 py-1 rounded-full">Most popular</span>
                )}
                <h3 className="font-heading text-2xl text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-sm font-medium text-brand-700 mb-4">{plan.tagline}</p>
                <div className="flex items-baseline gap-1 mb-5">
                  <span className="font-heading text-4xl text-gray-900">{plan.price || plan.priceLabel}</span>
                  {plan.price && <span className="text-gray-400 text-base">/mo</span>}
                </div>
                {plan.intro && (
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-4">{plan.intro}</p>
                )}
                <ul className="space-y-4 flex-1 mb-8">
                  {plan.features.map(([title, desc]) => (
                    <li key={title} className="flex gap-3">
                      <svg className="w-5 h-5 shrink-0 mt-0.5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                      </svg>
                      <div>
                        <div className="font-semibold text-sm text-gray-900">{title}</div>
                        <div className="text-sm leading-snug text-gray-500">{desc}</div>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* The $49 tier is self-serve — its own card says "Self-serve. No
                    call needed." — so it goes to signup + onboarding rather than the
                    book-a-call popup the other tiers still use. */}
                <button
                  onClick={() => (plan.selfServe ? (window.location.href = `/start/${planSlug(plan.key)}`) : openBook(plan.key))}
                  className={`w-full py-3 rounded-full font-semibold border transition-colors ${plan.highlight ? 'bg-brand-900 text-white border-transparent hover:bg-brand-800' : 'border-brand-300 text-brand-900 hover:bg-brand-50'}`}
                >
                  {plan.cta || 'Get Started'}
                </button>
                {plan.demo && (
                  <button
                    type="button"
                    onClick={() => setDemo(plan.demo)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm font-medium text-brand-700 hover:text-brand-900 transition-colors"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    {plan.demo.label}
                  </button>
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            Not sure which fits? <button onClick={() => openBook('unsure')} className="text-brand-700 font-medium underline hover:text-brand-800">Get started</button> &mdash; talk to Sangs and we&rsquo;ll figure it out together. Monthly, cancel anytime.
          </p>

          {/* Website service — one-liner */}
          <div className="mt-10">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center bg-[#faf0f0] border border-gray-100 rounded-2xl px-6 py-5">
              <span className="text-2xl" aria-hidden="true">🌐</span>
              <p className="text-gray-700 md:text-lg">
                <span className="font-semibold text-gray-900">Need a website?</span> We build or rebuild yours <span className="font-semibold text-brand-700">free</span> &mdash; and host it for a competitive fee.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200/80 text-brand-800 px-3 py-1 rounded-full text-xs font-medium mb-4 uppercase tracking-wider">
              How it works
            </div>
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900 mb-4">Two ways to get going</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Set up your own booking page in under 10 minutes &mdash; or hand us your class list
              and we&rsquo;ll have your whole studio running inside a day.
            </p>
          </div>

          <HowItWorks />

          {/* ─── White-glove onboarding callout ─── */}
          <div className="mt-16 md:mt-20 bg-white border border-brand-200/80 rounded-2xl p-8 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand-50 border border-brand-200/80 text-brand-800 px-3 py-1 rounded-full text-xs font-medium mb-4 uppercase tracking-wider">
                  White-glove setup &mdash; Studio Pro &amp; Custom
                </div>
                <h3 className="font-heading text-2xl md:text-3xl text-gray-900 mb-3">
                  On Studio or Custom? We set you up in under 24 hours.
                </h3>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Send us your class list and roster. We move every student into the right offering,
                  connect your website to the DanceHall back end, and build everything you need &mdash;
                  live in under 24 hours.
                </p>
                <button
                  type="button"
                  onClick={() => openBook('studio')}
                  className="bg-brand-900 text-white px-6 py-3 rounded-full font-semibold hover:bg-brand-800 transition-colors"
                >
                  Book white-glove setup
                </button>
              </div>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 bg-brand-50 border border-brand-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L10.16 3.66A2.25 2.25 0 008.568 3H9.568z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">We build your offerings &amp; connect your site</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Your programs, pricing, and website &mdash; wired into the DanceHall back end and pushed live.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 bg-brand-50 border border-brand-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Your whole roster, migrated</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      Every student moved into the right offering &mdash; and emailed to pay on your behalf. Done in under 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ─── */}
      <section className="py-20 md:py-28 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-3">In their own words</p>
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">What studio owners say about DanceHall</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure key={i} className="bg-[#faf0f0] rounded-2xl p-8 border border-gray-100 flex flex-col">
                <blockquote className="text-lg md:text-xl text-gray-800 leading-relaxed flex-1">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 text-sm text-gray-500">
                  &mdash; {t.name}{t.location ? `, ${t.location}` : ''}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 md:py-28 px-6 bg-white scroll-mt-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl text-gray-900">Questions & answers</h2>
          </div>

          <div className="divide-y divide-gray-100">
            {faqs.map((faq, i) => (
              <div key={i}>
                <button
                  onClick={() => toggleFaq(i)}
                  className="w-full flex items-center justify-between py-6 text-left group"
                  aria-expanded={openFaq === i}
                >
                  <span className="font-medium text-gray-900 pr-8 group-hover:text-brand-500 transition-colors">
                    {faq.q}
                  </span>
                  <svg
                    className={`w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-45' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </button>
                <div className={`overflow-hidden transition-all duration-200 ${openFaq === i ? 'max-h-48 pb-6' : 'max-h-0'}`}>
                  <p className="text-gray-500 leading-relaxed">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-brand-900 rounded-3xl px-8 py-16 md:px-16 md:py-24 text-center relative overflow-hidden">
            {/* Decorative gradients */}
            <div className="absolute inset-0 bg-gradient-to-br from-brand-800/50 via-transparent to-saffron-500/10 pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-700/30 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-saffron-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative">
              <h2 className="font-heading text-3xl md:text-5xl text-white mb-6 leading-tight">
                Ready to grow
                <br />
                your school?
              </h2>
              <p className="text-brand-200 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
                Tell us about your school, have a quick chat with Sangs, and pick a time.
                Your students can be enrolling and paying within days.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12">
                <button
                  type="button"
                  onClick={() => openBook()}
                  className="w-full sm:w-auto bg-white text-brand-900 border border-transparent px-10 py-4 rounded-full text-lg font-semibold hover:bg-brand-50 transition-colors hover:shadow-lg"
                >
                  Get Started
                </button>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-[#111111] text-gray-400 rounded-t-[2rem] mt-8">
        <div className="max-w-6xl mx-auto px-6 pt-16 pb-8">
          <div className="flex flex-col md:flex-row gap-12 md:gap-0 mb-16">
            {/* Logo — large, bold */}
            <div className="md:w-2/5">
              <Wordmark size="lg" tone="dark" />
            </div>

            {/* Links */}
            <div className="md:w-1/5">
              <ul className="space-y-3 text-sm">
                <li><a href="#pricing" className="hover:text-white transition-colors">Plans</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/blog" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Get Started</a></li>
              </ul>
            </div>

            {/* Company */}
            <div className="md:w-1/5">
              <div className="flex items-start gap-2 text-sm mb-3">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5M3.75 3v18m16.5-18v18M5.25 3h13.5M6 6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75m3-6.75h.75m-.75 3h.75m-.75 3h.75M6 18.75h12" />
                </svg>
                <a href="https://www.cbblabs.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  CBB Labs Inc.
                </a>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
                <span>447 Broadway FL 2, New York, NY</span>
              </div>
            </div>

            {/* Follow Us */}
            <div className="md:w-1/5 md:text-right">
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-4">Follow Us</p>
              <div className="flex items-center gap-3 md:justify-end">
                <a
                  href="https://www.instagram.com/my.dancehall/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-gray-700 flex items-center justify-center hover:border-white hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="w-[18px] h-[18px]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-gray-800 pt-6 text-center">
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} DanceHall Studios. All rights reserved.
            </p>
            <p className="mt-2 text-xs text-gray-600">
              <a href="/terms" className="hover:text-white transition-colors">Terms</a> · <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            </p>
          </div>
        </div>
      </footer>

      <Suspense fallback={null}><AskSangs surface="public" /></Suspense>

      <BookCallModal open={bookOpen} initialPlan={bookPlan} onClose={() => setBookOpen(false)} />
      {demo && <Suspense fallback={null}><DemoVideoModal demo={demo} onClose={() => setDemo(null)} /></Suspense>}
    </div>
  );
};

export default LandingPage;
